/**
 * Offline storage for journal app: IndexedDB queue + snapshot for offline-first support.
 * - Pending ops queue: stores write operations to replay when back online
 * - Snapshot: full state for hydrating app when opened offline
 */

import { openDB } from 'idb';

const DB_NAME = 'life-journal-offline';
const DB_VERSION = 1;
const STORE_QUEUE = 'pending_ops';
const STORE_SNAPSHOT = 'snapshot';
const STORE_PENDING_IMAGES = 'pending_images';

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
          db.createObjectStore(STORE_SNAPSHOT, { keyPath: 'userId' });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING_IMAGES)) {
          db.createObjectStore(STORE_PENDING_IMAGES, { keyPath: 'dateKey' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Enqueue a pending operation to be replayed when online.
 * @param {Object} op - { type, payload, timestamp }
 */
export async function enqueue(op) {
  const db = await getDB();
  await db.add(STORE_QUEUE, {
    ...op,
    timestamp: op.timestamp || Date.now(),
  });
}

/**
 * Get all pending operations.
 */
export async function getQueue() {
  const db = await getDB();
  return db.getAll(STORE_QUEUE);
}

/**
 * Remove operations from the queue by id.
 * @param {number[]} ids - ids to remove
 */
export async function dequeue(ids) {
  const db = await getDB();
  const tx = db.transaction(STORE_QUEUE, 'readwrite');
  await Promise.all(ids.map((id) => tx.store.delete(id)));
  await tx.done;
}

/**
 * Clear the entire queue (e.g. after successful sync).
 */
export async function clearQueue() {
  const db = await getDB();
  await db.clear(STORE_QUEUE);
}

/**
 * Save full state snapshot for offline hydration.
 * @param {string} userId - user id
 * @param {Object} snapshot - { entries, habitsByMonth, habitLog, sleepLog, moodLog, weightLog }
 */
export async function saveSnapshot(userId, snapshot) {
  if (!userId) return;
  const db = await getDB();
  await db.put(STORE_SNAPSHOT, {
    userId,
    ...snapshot,
    savedAt: Date.now(),
  });
}

/**
 * Load snapshot for given user (when opening app offline).
 */
export async function loadSnapshot(userId) {
  if (!userId) return null;
  const db = await getDB();
  return db.get(STORE_SNAPSHOT, userId);
}

/**
 * Store a pending image blob for upload when online.
 * @param {string} userId - user id
 * @param {string} dateKey - date key
 * @param {Blob} blob - compressed image blob
 */
export async function savePendingImage(userId, dateKey, blob) {
  const db = await getDB();
  await db.put(STORE_PENDING_IMAGES, {
    dateKey,
    userId,
    blob,
    timestamp: Date.now(),
  });
}

/**
 * Get all pending images.
 */
export async function getPendingImages() {
  const db = await getDB();
  return db.getAll(STORE_PENDING_IMAGES);
}

/**
 * Remove a pending image after successful upload.
 */
export async function removePendingImage(dateKey) {
  const db = await getDB();
  await db.delete(STORE_PENDING_IMAGES, dateKey);
}
